import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiBell, FiX, FiAlertTriangle, FiInfo, FiZap, FiChevronRight, FiCheck } from 'react-icons/fi';
import { fetchAPI } from '../../utils/api';

const NoticeBell = () => {
    const [notices, setNotices] = useState([]);
    const [isOpen, setIsOpen] = useState(false);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const getNotices = async () => {
            try {
                const data = await fetchAPI('/public/get_active_notices.php');
                const activeNotices = data.filter(n => n.priority === 'urgent' || n.priority === 'high' || n.priority === 'medium');
                setNotices(activeNotices);

                // Auto-popup logic
                if (activeNotices.length > 0) {
                    const latestId = activeNotices[0].id;
                    const lastSeenId = localStorage.getItem('lastSeenNoticeId');
                    const hasSeenThisVisit = sessionStorage.getItem('noticeAutoShown');

                    if (latestId.toString() !== lastSeenId && !hasSeenThisVisit) {
                        setIsOpen(true);
                        localStorage.setItem('lastSeenNoticeId', latestId);
                        sessionStorage.setItem('noticeAutoShown', 'true');
                    }
                }
            } catch (error) {
                console.error('Error fetching notices:', error);
            } finally {
                setLoading(false);
            }
        };
        getNotices();

        const interval = setInterval(getNotices, 300000);
        return () => clearInterval(interval);
    }, []);

    const getPriorityIcon = (priority) => {
        switch (priority) {
            case 'urgent': return <FiZap className="text-red-500" />;
            case 'high': return <FiAlertTriangle className="text-orange-500" />;
            default: return <FiInfo className="text-blue-500" />;
        }
    };

    return (
        <>
            {/* Pulsing Bell Button */}
            <div className="fixed bottom-36 right-8 z-[2000]">
                <motion.button
                    whileHover={{ scale: 1.1, rotate: [0, -10, 10, -10, 10, 0] }}
                    whileTap={{ scale: 0.9 }}
                    onClick={() => setIsOpen(true)}
                    className="relative w-14 h-14 rounded-full bg-blue-600 flex items-center justify-center text-white shadow-[0_0_30px_rgba(59,130,246,0.5)] border border-blue-400/50 group"
                >
                    <FiBell className="text-2xl" />
                    {notices.length > 0 && (
                        <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full border-2 border-[#0a0a0a] text-[10px] font-black flex items-center justify-center animate-pulse">
                            {notices.length}
                        </span>
                    )}
                </motion.button>
            </div>

            {/* Central Modal */}
            <AnimatePresence>
                {isOpen && (
                    <div className="fixed inset-0 z-[2001] flex items-center justify-center p-4 md:p-8">
                        {/* Master Backdrop */}
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={() => setIsOpen(false)}
                            className="absolute inset-0 bg-black/60 backdrop-blur-md cursor-pointer"
                        ></motion.div>

                        {/* Modal Container */}
                        <motion.div
                            initial={{ opacity: 0, scale: 0.9, y: 30 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.9, y: 30 }}
                            className="relative max-h-[600px] w-full max-w-[900px] bg-[#0d0d0d]/95 rounded-[1.5rem] border border-white/10 shadow-[0_50px_100px_rgba(0,0,0,0.8)] overflow-hidden flex flex-col items-center py-10 md:py-16 px-6 md:px-12"
                        >
                            {/* Close Button */}
                            <button
                                onClick={() => setIsOpen(false)}
                                className="absolute top-8 right-8 w-12 h-12 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center text-white/40 hover:text-white transition-all z-20 cursor-pointer"
                            >
                                <FiX size={24} />
                            </button>

                            {/* Live Badge */}
                            <div className="bg-blue-500/10 border border-blue-500/20 px-4 py-1.5 rounded-full flex items-center gap-1 mb-6">
                                <div className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse"></div>
                                <span className="text-[10px] font-black uppercase tracking-[0.2em] text-blue-500">Live Updates</span>
                            </div>

                            {/* Header */}
                            <h2 className="text-4xl md:text-5xl font-black text-white mb-12 text-center tracking-tight">
                                Academic <span className="text-blue-500">Registry</span>
                            </h2>

                            {/* Notice Grid */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 w-full overflow-y-auto mb-4 px-2 custom-scrollbar">
                                {loading ? (
                                    <div className="col-span-full py-20 flex flex-col items-center justify-center opacity-20">
                                        <div className="w-8 h-8 border-2 border-blue-500/20 border-t-blue-500 rounded-full animate-spin mb-4"></div>
                                        <span className="text-[10px] font-black uppercase tracking-widest text-white">Accessing Data...</span>
                                    </div>
                                ) : notices.length > 0 ? (
                                    notices.map((notice, i) => (
                                        <motion.div
                                            key={notice.id}
                                            initial={{ opacity: 0, x: i % 2 === 0 ? -20 : 20 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            transition={{ delay: i * 0.1 }}
                                            className="bg-white/[0.03] border border-white/5 rounded-[1.5rem] p-6 hover:bg-white/[0.06] transition-all group flex flex-col justify-between"
                                        >
                                            <div className="flex justify-between items-start mb-4">
                                                <div className="flex items-center gap-2">
                                                    <div className="w-8 h-8 rounded-xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-center shadow-sm">
                                                        {getPriorityIcon(notice.priority)}
                                                    </div>
                                                    <div>
                                                        <p className="text-[9px] font-black text-white/30 uppercase tracking-[0.1em]">{notice.timeAgo}</p>
                                                        <p className="text-[10px] font-black text-blue-500 uppercase tracking-tight">{notice.priority}</p>
                                                    </div>
                                                </div>
                                                {notice.priority === 'urgent' && (
                                                    <div className="w-5 h-5 rounded-full bg-red-500 text-white flex items-center justify-center text-[8px] font-black animate-pulse">!</div>
                                                )}
                                            </div>

                                            <h4 className="text-lg font-black text-white leading-tight mb-3 uppercase italic tracking-tighter group-hover:text-blue-400 transition-colors">
                                                {notice.title}
                                            </h4>

                                            <p className="text-xs text-white/40 font-medium line-clamp-2 mb-4 leading-relaxed">
                                                {notice.content}
                                            </p>

                                            <div className="flex items-center gap-2 text-[10px] font-black text-blue-500 uppercase tracking-widest opacity-60 group-hover:opacity-100 transition-opacity">
                                                <span>Active Protocol</span>
                                                <FiChevronRight className="group-hover:translate-x-1 transition-transform" />
                                            </div>
                                        </motion.div>
                                    ))
                                ) : (
                                    <div className="col-span-full py-20 text-center opacity-20">
                                        <FiInfo size={40} className="mx-auto mb-4 text-white" />
                                        <p className="text-[10px] font-black uppercase tracking-[0.3em] text-white">No urgent alerts found in the current registry cycle.</p>
                                    </div>
                                )}
                            </div>

                            {/* Global CTA - Commented as per user preference */}
                            {/* <motion.button
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                                onClick={() => setIsOpen(false)}
                                className="bg-blue-600 text-white px-12 py-5 rounded-2xl flex items-center gap-3 font-black uppercase tracking-[0.2em] text-xs shadow-2xl shadow-blue-600/20 group transition-all hover:bg-blue-500"
                            >
                                <FiZap className="group-hover:animate-pulse" />
                                <span>Acknowledge All Updates</span>
                            </motion.button> */}

                            {/* Decorative Elements */}
                            <div className="absolute bottom-0 left-0 w-full h-32 bg-gradient-to-t from-blue-500/5 to-transparent pointer-events-none"></div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>

            <style dangerouslySetInnerHTML={{
                __html: `
                .custom-scrollbar::-webkit-scrollbar {
                    width: 4px;
                }
                .custom-scrollbar::-webkit-scrollbar-track {
                    background: transparent;
                }
                .custom-scrollbar::-webkit-scrollbar-thumb {
                    background: rgba(255, 255, 255, 0.05);
                    border-radius: 10px;
                }
                .custom-scrollbar::-webkit-scrollbar-thumb:hover {
                    background: rgba(59, 130, 246, 0.5);
                }
            ` }} />
        </>
    );
};

export default NoticeBell;
